import { useState } from "react";
import { FaPlus } from "react-icons/fa";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { ConfirmModal } from "../../components/Modals/ConfirmModal";
import { Paragraph } from "../../components/Paragraph";
import { Table } from "../../components/Table";
import { TableCell } from "../../components/Table/TableCell";
import { TableRow } from "../../components/Table/TableRow";
import { Toggle } from "../../components/Toggle";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { TableHead } from "../../components/Table/TableHead";
import { TableBody } from "../../components/Table/TableBody";
import deleteIcon from "../../assets/settings/delete-red.svg";
import editIcon from "../../assets/settings/edit.svg";
import { Image } from "../../components/Image";
import { AddEditConsultantsModal } from "../../components/Modals/AddEditConsultantsModal";
import { useConsultants } from "../../data/useConsultants";
import { Consultant } from "../../types/Consultant";
import { useUpdateConsultantDetails } from "../../data/useUpdateConsultantDetails";
import { useDeleteConsultant } from "../../data/useDeleteConsultant";

const SettingsConsultants = () => {
  // TODO might need to paginate, but out of scope for now.
  const { data: consultant, isSuccess } = useConsultants();
  const hasData = isSuccess && consultant?.data?.count > 0;
  const { mutate: updateConsultant } = useUpdateConsultantDetails();
  const { mutate: deleteConsultant } = useDeleteConsultant();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [selectedConsultantId, setSelectedConsultantId] = useState(null);

  const handleAddConsultant = () => {
    setIsEditModalOpen(true);
    setSelectedConsultantId(null);
  };

  const handleEditConsultant = (id) => () => {
    setIsEditModalOpen(true);
    setSelectedConsultantId(id);
  };

  const handleDeleteConsultant = (id) => () => {
    setIsConfirmDeleteOpen(true);
    setSelectedConsultantId(id);
  };

  const optimisticUpdate = (
    consultantId: number,
    fieldName: keyof Consultant,
    value: any
  ) => {
    queryClient.setQueryData(["consultant"], (old: any) => {
      const newConsultant = old.data.results.map((consultant) => {
        if (consultant.id === consultantId) {
          return { ...consultant, [fieldName]: value };
        }
        return consultant;
      });

      return {
        ...old,
        data: {
          ...old.data,
          results: newConsultant,
        },
      };
    });
  };

  const handleUpdateConsultantActive = (consultantId: number, isActive: boolean) => {
    optimisticUpdate(consultantId, "active", isActive);

    updateConsultant(
      {
        id: consultantId,
        active: isActive,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["consultants"]);
         // queryClient.invalidateQueries(["consultantDetails", consultantId]);
        },
        onError: (err: any) => {
          showError(err, "A problem occurred while updating the consultant.");
        },
      }
    );
  };

  const confirmDeleteConsultant = () => {
    setIsConfirmDeleteOpen(false);
    deleteConsultant(
      { id: selectedConsultantId },
      {
        onSuccess: () => {
          notify("Consultant Deleted", {
            type: "success",
          });
          queryClient.invalidateQueries(["consultant"]);
        },
        onError: (err: any) => {
          showError(err, "A problem occurred while updating the consultant.");
        },
        onSettled: () => {
          setSelectedConsultantId(null);
        },
      }
    );
  };

  return (
    <Box bg="white" borderRadius={0} p={3} mb={3}>
      <Flex justifyContent="space-between" alignItems="center">
        <H2>Consultants</H2>
        <Button onClick={handleAddConsultant} Icon={FaPlus} iconSize="16px">
         Create Consultants
        </Button>
      </Flex>

      {!hasData && <Paragraph textAlign="center">No Consultants</Paragraph>}

      {hasData && (
        <Box mt={5}>
          <Table>
            <TableHead></TableHead>
            <TableBody>
              <TableRow>
                <TableCell isHeading>Name</TableCell>
                <TableCell isHeading>Email Address</TableCell>
                <TableCell isHeading>Active</TableCell>
                <TableCell />
                <TableCell />
              </TableRow>
              {consultant?.data.results.map(
                ({
                  name,
                  email,
                  id,
                  active,
                }) => (
                  <TableRow key={id}>
                    <TableCell>
                      {name}
                    </TableCell>
                    <TableCell>{email}</TableCell>
                    <TableCell >
                        <Toggle
                          isChecked={active}
                          onChange={(e) =>
                            handleUpdateConsultantActive(id, e.target.checked)
                          }
                        />
                    </TableCell>
                    <TableCell >
                    <Image
                        src={editIcon}
                        alt="edit icon"
                        height="24px"
                        width="24px"
                        marginRight={"4px"}
                        onClick={handleEditConsultant(id)}
                        style={{cursor: "pointer"}}
                      />
                    </TableCell>
                    <TableCell >
                        <Image
                        src={deleteIcon}
                        alt="delete icon"
                        height="24px"
                        width="24px"
                        marginRight={"4px"}
                        onClick={handleDeleteConsultant(id)}
                        style={{cursor: "pointer"}}
                      />
                        
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </Box>
      )}
      <AddEditConsultantsModal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        consultantId={selectedConsultantId}
      />
      <ConfirmModal
        isOpen={isConfirmDeleteOpen}
        title="Are you sure you want to delete this consultant?"
        onRequestClose={() => {
          setSelectedConsultantId(null);
          setIsConfirmDeleteOpen(false);
        }}
        handleConfirm={confirmDeleteConsultant}
        content="This action is irreversible and will remove them from all associated applications."
      />
    </Box>
  );
};

export default SettingsConsultants;
